// Styles
import '@scss/variables.scss';
import '../../MapStyles/MapMenu.scss';

// Libraries
import classNames from 'classnames';
import { useEffect } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';

// Components
import { toast } from '@components';
import { V2FilterMapList } from '../../../../../../components/AssetFilter/functions/V2FilterMapList';
import { MapInfoItem } from './MapInfoItem';
import { MapInfoList } from './MapInfoList';
import { MapInfoOverlay } from './MapInfoOverlay';
import { MapInfoTabs } from './MapInfoTabs';
import { MapInfoTitle } from './MapInfoTitle';

export function MapInfoMenu() {
  const history = useHistory();

  // Map Info
  const mapID = useMapStore((state) => state.mapID);
  const mapName = useMapStore((state) => state.mapName);
  const setIsHydrating = useMapStore((state) => state.setIsHydrating);

  // UI State / Interactions
  const showInfo = useMapStore((state) => state.showInfo);
  const setShowInfo = useMapStore((state) => state.setShowInfo);
  const selected = useMapStore((state) => state.selected);
  const showMore = useMapStore((state) => state.showMore);

  // Overlay Data
  const overlayFC = useMapStore((state) => state.overlayFC);

  // Assets
  const assetFC = useMapStore((state) => state.assetFC);
  const assetList = useMapStore((state) => state.assetList);
  const assetLayer = useMapStore((state) => state.assetLayer);
  const setAssetFC = useMapStore((state) => state.setAssetFC);
  const setAssetList = useMapStore((state) => state.setAssetList);
  const assetRefLayer = useMapStore((state) => state.assetRefLayer);
  const fetchMapAssets = useMapStore((state) => state.fetchMapAssets);
  const resetAsset = useMapStore((state) => state.resetAsset);

  // Places
  const placeFC = useMapStore((state) => state.placeFC);
  const placeList = useMapStore((state) => state.placeList);
  const placeLayer = useMapStore((state) => state.placeLayer);
  const setPlaceFC = useMapStore((state) => state.setPlaceFC);
  const setPlaceList = useMapStore((state) => state.setPlaceList);
  const placeRefLayer = useMapStore((state) => state.placeRefLayer);
  const fetchMapPlaces = useMapStore((state) => state.fetchMapPlaces);
  const resetPlace = useMapStore((state) => state.resetPlace);

  // Gateways
  const gatewayFC = useMapStore((state) => state.gatewayFC);
  const gatewayList = useMapStore((state) => state.gatewayList);
  const gatewayLayer = useMapStore((state) => state.gatewayLayer);
  const setGatewayFC = useMapStore((state) => state.setGatewayFC);
  const setGatewayList = useMapStore((state) => state.setGatewayList);
  const gatewayRefLayer = useMapStore((state) => state.gatewayRefLayer);
  const fetchMapGateways = useMapStore((state) => state.fetchMapGateways);
  const resetGateway = useMapStore((state) => state.resetGateway);

  // Stationary Assets
  const stationaryFC = useMapStore((state) => state.stationaryFC);
  const stationaryList = useMapStore((state) => state.stationaryList);
  const stationaryLayer = useMapStore((state) => state.stationaryLayer);
  const setStationaryFC = useMapStore((state) => state.setStationaryFC);
  const setStationaryList = useMapStore((state) => state.setStationaryList);
  const stationaryRefLayer = useMapStore((state) => state.stationaryRefLayer);
  const fetchMapStationary = useMapStore((state) => state.fetchMapStationary);
  const resetStationary = useMapStore((state) => state.resetStationary);

  let infoClasses = classNames({
    'info-container': !showInfo,
    'info-container-hide': showInfo,
  });

  let collapseClasses = classNames({
    'collapse-button': showInfo,
    'collapse-button-hide': !showInfo,
  });

  // --- Map Data Hydration ---
  useEffect(() => {
    if (!mapID) return;

    if (placeFC !== null && gatewayFC !== null && stationaryFC !== null) return;

    async function hydrateMapStore() {
      const defaultPayload = V2FilterMapList([], 'and', { field: 'Name', ascending: true }, '');

      try {
        const [places, gateways, stationary] = await Promise.all([
          fetchMapPlaces(defaultPayload),
          fetchMapGateways(defaultPayload),
          fetchMapStationary(defaultPayload),
        ]);

        setPlaceFC(places);
        setPlaceList(places);

        setGatewayFC(gateways);
        setGatewayList(gateways);

        setStationaryFC(stationary);
        setStationaryList(stationary);
      } catch (error) {
        console.error('Failed to hydrate initial map info data:', error);
        toast.error('Failed to load map data.');
        history.replace('/maps');
      }
    }

    hydrateMapStore();
  }, [mapID, placeFC, gatewayFC, stationaryFC]);

  return (
    <>
      <div className={collapseClasses}>
        <Card style={{ backgroundColor: 'var(--primary)' }}>
          <Button
            variant="dark"
            type="submit"
            className="collapse-card"
            style={{
              borderTopRightRadius: '0px',
              borderBottomRightRadius: '0px',
              width: '50px',
            }}
            onClick={() => {
              setShowInfo(!showInfo);
            }}
          >
            <i
              className={'sonarIcon sonarIcon-right-arrow align-sub fs-lg'}
              style={{
                transform: showInfo ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.5s ease-in-out',
                color: 'var(--white)',
              }}
            />
          </Button>
        </Card>
      </div>

      <div className={infoClasses}>
        <Card className="d-flex flex-row" style={{ backgroundColor: 'var(--primary)' }}>
          <div className="collapse-container">
            <Button
              variant="dark"
              type="submit"
              className="collapse-card"
              style={{
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                width: '50px',
              }}
              onClick={() => {
                setShowInfo(!showInfo);
              }}
            >
              <i
                className={'sonarIcon sonarIcon-right-arrow align-sub fs-lg'}
                style={{
                  transform: showInfo ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.5s ease-in-out',
                  color: 'var(--white)',
                }}
              />
            </Button>
          </div>
          <div className="menu-bar">
            <MapInfoTitle
              mapType={overlayFC?.features[0]?.properties?.mapType}
              mapTitle={mapName}
            />
            <MapInfoTabs />
          </div>
        </Card>
        <Card
          className={selected === '' ? 'object-list' : 'object-list-expand'}
          style={{
            backgroundColor: 'var(--light)',
            borderTopLeftRadius: '0px',
            borderTopRightRadius: '0px',
          }}
        >
          <MapInfoOverlay
            mapData={overlayFC}
            visible={selected === 'overlay' && showMore === null}
          />
          <MapInfoList
            key="asset"
            type="asset"
            listData={assetList}
            listMarkers={assetLayer}
            listRefs={assetRefLayer}
            visible={selected === 'asset' && showMore === null}
            disabledFilters={['name', 'map', 'single asset', 'device id', 'place', 'battery value']}
            fetchData={async (e) => {
              const data = await fetchMapAssets(e);
              setAssetFC(data);
              setAssetList(data);
            }}
            reset={resetAsset}
          />
          <MapInfoList
            key="place"
            type="place"
            listData={placeList}
            listMarkers={placeLayer}
            listRefs={placeRefLayer}
            visible={selected === 'place' && showMore === null}
            disabledFilters={[
              'name',
              'map',
              'single asset',
              'device id',
              'place',
              'battery value',
              'identifier',
              'active assets',
              'inactive assets',
            ]}
            reset={resetPlace}
          />
          <MapInfoList
            key="gateway"
            type="gateway"
            listData={gatewayList}
            listMarkers={gatewayLayer}
            listRefs={gatewayRefLayer}
            visible={selected === 'gateway' && showMore === null}
            disabledFilters={[
              'name',
              'map',
              'single asset',
              'place',
              'keyword',
              'identifier',
              'battery value',
              'active assets',
              'inactive assets',
            ]}
            reset={resetGateway}
          />
          <MapInfoList
            key="stationary"
            type="stationary"
            listData={stationaryList}
            listMarkers={stationaryLayer}
            listRefs={stationaryRefLayer}
            visible={selected === 'stationary' && showMore === null}
            disabledFilters={['name', 'map', 'single asset', 'device id', 'place', 'battery value']}
            reset={resetStationary}
          />

          {showMore !== null && <MapInfoItem />}
        </Card>
      </div>
    </>
  );
}
